import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Shield, Loader2, Plus, Trash2, Users, ChevronDown, ChevronRight, Pencil } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { PERMISSION_GROUPS, type PermissionKey, type ChurchRole } from "@/hooks/usePermissions";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export default function RolePermissionsPanel() {
  const { profile } = useChurch();
  const queryClient = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [localPerms, setLocalPerms] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editRoleName, setEditRoleName] = useState("");
  const [editRoleDesc, setEditRoleDesc] = useState("");
  const [roleToDelete, setRoleToDelete] = useState<ChurchRole | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [localScopedMinistryIds, setLocalScopedMinistryIds] = useState<string[]>([]);

  // Fetch church roles
  const { data: churchRoles = [], isLoading: loadingRoles } = useQuery({
    queryKey: ["church_roles", profile?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("church_roles")
        .select("*")
        .eq("church_id", profile!.id)
        .order("system_level", { ascending: true })
        .order("name", { ascending: true });
      if (error) throw error;
      return (data || []) as ChurchRole[];
    },
    enabled: !!profile?.id,
  });

  // Count users per role
  const { data: userCounts = {} } = useQuery({
    queryKey: ["role_user_counts", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role_id")
        .eq("church_id", profile!.id);
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data || []).forEach((ur: any) => {
        if (ur.role_id) {
          counts[ur.role_id] = (counts[ur.role_id] || 0) + 1;
        }
      });
      return counts;
    },
    enabled: !!profile?.id,
  });

  const { data: ministries = [] } = useQuery({
    queryKey: ["ministries_for_role_scope", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, type")
        .eq("church_id", profile!.id)
        .eq("type", "ministry")
        .order("name", { ascending: true });

      if (error) throw error;
      return (data || []) as { id: string; name: string; type: string }[];
    },
    enabled: !!profile?.id,
  });

  // Auto-select first non-admin role, or first role
  useEffect(() => {
    if (!selectedRoleId && churchRoles.length > 0) {
      const customRole = churchRoles.find((r) => r.system_level !== "church_admin");
      setSelectedRoleId((customRole || churchRoles[0]).id);
    }
  }, [churchRoles, selectedRoleId]);

  // Build local permissions from selected role
  useEffect(() => {
    const role = churchRoles.find((r) => r.id === selectedRoleId);
    if (!role) return;
    const perms: Record<string, boolean> = {};
    PERMISSION_GROUPS.forEach((group) => {
      group.permissions.forEach((p) => {
        perms[p.key] = role[p.key] === true;
      });
    });
    setLocalPerms(perms);
    const scopedIds = (role as any).scoped_ministry_ids;
    setLocalScopedMinistryIds(Array.isArray(scopedIds) ? scopedIds.filter((id) => typeof id === "string") : []);
    setHasChanges(false);
  }, [selectedRoleId, churchRoles]);

  const selectedRole = churchRoles.find((r) => r.id === selectedRoleId);
  const isAdminRole = selectedRole?.system_level === "church_admin";

  const togglePermission = (key: string) => {
    if (isAdminRole) return;
    setLocalPerms((prev) => ({ ...prev, [key]: !prev[key] }));
    setHasChanges(true);
  };

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupLabel)) next.delete(groupLabel);
      else next.add(groupLabel);
      return next;
    });
  };

  const toggleScopedMinistry = (organizationId: string) => {
    if (isAdminRole) return;
    setLocalScopedMinistryIds((prev) => {
      const next = prev.includes(organizationId)
        ? prev.filter((id) => id !== organizationId)
        : [...prev, organizationId];
      return next;
    });
    setHasChanges(true);
  };

  // Toggle all permissions in a group
  const toggleGroupAll = (groupLabel: string) => {
    if (isAdminRole) return;
    const group = PERMISSION_GROUPS.find((g) => g.label === groupLabel);
    if (!group) return;
    const allEnabled = group.permissions.every((p) => localPerms[p.key]);
    const updates: Record<string, boolean> = {};
    group.permissions.forEach((p) => {
      updates[p.key] = !allEnabled;
    });
    setLocalPerms((prev) => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  // Save permissions
  const saveMutation = useMutation({
    mutationFn: async () => {
      const updates: Record<string, boolean> = {};
      PERMISSION_GROUPS.forEach((group) => {
        group.permissions.forEach((p) => {
          updates[p.key] = localPerms[p.key] ?? false;
        });
      });
      const payload: Record<string, any> = {
        ...updates,
        scoped_ministry_ids: localScopedMinistryIds,
      };
      const { error } = await (supabase as any)
        .from("church_roles")
        .update(payload)
        .eq("id", selectedRoleId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["church_roles"] });
      queryClient.invalidateQueries({ queryKey: ["church_role"] });
      
      setHasChanges(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Create role
  const createRoleMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await (supabase as any)
        .from("church_roles")
        .insert({
          church_id: profile!.id,
          name: newRoleName.trim(),
          description: newRoleDesc.trim() || null,
          system_level: "custom",
        })
        .select("id")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["church_roles"] });
      
      setNewRoleName("");
      setNewRoleDesc("");
      setShowCreateDialog(false);
      setSelectedRoleId(data.id);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Delete role
  const deleteRoleMutation = useMutation({
    mutationFn: async (role: ChurchRole) => {
      const count = userCounts[role.id] || 0;
      if (count > 0) throw new Error(`Cannot delete: ${count} user(s) are assigned this role`);
      const { error } = await (supabase as any)
        .from("church_roles")
        .delete()
        .eq("id", role.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["church_roles"] });
      
      setSelectedRoleId("");
      setRoleToDelete(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateRoleMutation = useMutation({
    mutationFn: async () => {
      if (!selectedRoleId) throw new Error("No role selected");
      const { error } = await (supabase as any)
        .from("church_roles")
        .update({
          name: editRoleName.trim(),
          description: editRoleDesc.trim() || null,
        })
        .eq("id", selectedRoleId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["church_roles"] });
      
      setShowEditDialog(false);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold text-foreground">Role Builder</h3>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowCreateDialog(true)}>
          <Plus className="w-4 h-4 mr-1" /> New Role
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Create custom roles and configure granular permissions. Church Admin always has full access.
      </p>

      {/* Role selector */}
      <div className="flex flex-wrap gap-2 mb-6">
        {churchRoles.map((r) => (
          <button
            key={r.id}
            onClick={() => setSelectedRoleId(r.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedRoleId === r.id
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
          >
            {r.name}
            {r.system_level === "church_admin" && (
              <span className="ml-1.5 text-[10px] opacity-70"></span>
            )}
            {userCounts[r.id] ? (
              <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">
                <Users className="w-3 h-3 mr-0.5" />{userCounts[r.id]}
              </Badge>
            ) : null}
          </button>
        ))}
      </div>

      {loadingRoles ? (
        <div className="flex justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </div>
      ) : selectedRole ? (
        <>
          {/* Role info + delete */}
          <div className="flex items-center justify-between mb-4 p-3 rounded-lg bg-muted/50">
            <div>
              <p className="text-sm font-medium text-foreground">{selectedRole.name}</p>
              {selectedRole.description && (
                <p className="text-xs text-muted-foreground">{selectedRole.description}</p>
              )}
            </div>
            {!isAdminRole && (
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="hover:bg-secondary/70"
                  onClick={() => {
                    setEditRoleName(selectedRole.name || "");
                    setEditRoleDesc(selectedRole.description || "");
                    setShowEditDialog(true);
                  }}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={() => setRoleToDelete(selectedRole)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {isAdminRole && (
            <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/10">
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Church Admin</strong> has full access to all features and cannot be restricted.
              </p>
            </div>
          )}

          {!isAdminRole && (
            <div className="mb-4 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-foreground">Ministry Scope</p>
                <Badge variant="outline" className="text-[10px]">{localScopedMinistryIds.length} selected</Badge>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Select ministries this role can access. Scoped pages only show records for selected ministries.
              </p>
              {ministries.length === 0 ? (
                <p className="text-xs text-muted-foreground">No ministries available yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                  {ministries.map((ministry) => (
                    <label
                      key={ministry.id}
                      className="flex items-center gap-2 rounded-md border border-border px-2.5 py-2 hover:bg-muted/30 cursor-pointer"
                    >
                      <Checkbox
                        checked={localScopedMinistryIds.includes(ministry.id)}
                        onCheckedChange={() => toggleScopedMinistry(ministry.id)}
                        className="h-3.5 w-3.5 md:h-4 md:w-4 !rounded-[3px]"
                      />
                      <span className="text-sm text-foreground">{ministry.name}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Permission groups */}
          <div className="space-y-2">
            {PERMISSION_GROUPS.map((group) => {
              const isExpanded = expandedGroups.has(group.label);
              const enabledCount = group.permissions.filter((p) => isAdminRole || localPerms[p.key]).length;
              const totalCount = group.permissions.length;

              return (
                <Collapsible key={group.label} open={isExpanded} onOpenChange={() => toggleGroup(group.label)}>
                  <div className="rounded-lg border border-border">
                    <div className="flex items-center justify-between px-4 py-3">
                      <CollapsibleTrigger asChild>
                        <button className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          {group.label}
                          <Badge variant="outline" className="text-[10px] ml-1">
                            {enabledCount}/{totalCount}
                          </Badge>
                        </button>
                      </CollapsibleTrigger>
                      {!isAdminRole && (
                        <button
                          onClick={() => toggleGroupAll(group.label)}
                          className="text-[11px] text-primary hover:underline"
                        >
                          {enabledCount === totalCount ? "Disable all" : "Enable all"}
                        </button>
                      )}
                    </div>
                    <CollapsibleContent>
                      <div className="border-t border-border">
                        {group.permissions.map((perm) => (
                          <div key={perm.key} className="flex items-center justify-between px-4 py-2.5 hover:bg-muted/30 transition-colors">
                            <div>
                              <p className="text-sm text-foreground">{perm.label}</p>
                              <p className="text-xs text-muted-foreground">{perm.description}</p>
                            </div>
                            <Switch
                              checked={isAdminRole ? true : (localPerms[perm.key] ?? false)}
                              onCheckedChange={() => togglePermission(perm.key)}
                              disabled={isAdminRole}
                            />
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              );
            })}
          </div>

          {/* Save button */}
          {hasChanges && !isAdminRole && (
            <div className="mt-4 pt-4 border-t border-border">
              <Button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                size="sm"
              >
                {saveMutation.isPending ? "Saving..." : "Save Permissions"}
              </Button>
            </div>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-8">
          No roles created yet. Click "New Role" to get started.
        </p>
      )}

      {/* Create Role Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogDescription>
              Define a custom role with its own permission set. Examples: Treasurer, Secretary, Ministry Leader.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Role Name</label>
              <Input
                placeholder="e.g. Treasurer, Secretary, Usher Captain"
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Description (optional)</label>
              <Input
                placeholder="Brief description of this role's responsibilities"
                value={newRoleDesc}
                onChange={(e) => setNewRoleDesc(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button
              onClick={() => createRoleMutation.mutate()}
              disabled={!newRoleName.trim() || createRoleMutation.isPending}
            >
              {createRoleMutation.isPending ? "Creating..." : "Create Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Role Dialog */}
      <Dialog open={!!roleToDelete} onOpenChange={() => setRoleToDelete(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{roleToDelete?.name}"?
              {(userCounts[roleToDelete?.id || ""] || 0) > 0 && (
                <span className="block mt-2 text-destructive font-medium">
                  ⚠️ {userCounts[roleToDelete?.id || ""]} user(s) are assigned this role. Reassign them first.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleToDelete(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => roleToDelete && deleteRoleMutation.mutate(roleToDelete)}
              disabled={deleteRoleMutation.isPending || (userCounts[roleToDelete?.id || ""] || 0) > 0}
            >
              {deleteRoleMutation.isPending ? "Deleting..." : "Delete Role"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Role</DialogTitle>
            <DialogDescription>
              Update the role name and description.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Role Name</label>
              <Input
                placeholder="e.g. Youth Secretary"
                value={editRoleName}
                onChange={(e) => setEditRoleName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Description (optional)</label>
              <Input
                placeholder="Brief description of this role's responsibilities"
                value={editRoleDesc}
                onChange={(e) => setEditRoleDesc(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button
              onClick={() => updateRoleMutation.mutate()}
              disabled={!editRoleName.trim() || updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
